import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useStickyHeader } from '@/hooks/useStickyHeader';
import { useStickyScrollbar } from '@/hooks/useStickyScrollbar';
import { Icon } from '@/components/Icon';
import { Icons } from '@/assets';

export const TableV3 = ({
  headers = [],
  data = [],
  emptyMessage = '',
  minWidth,
  stickyHeader = true,
  appHeaderHeight = 64,
  onSort,
  stickyScrollbar = true,
  scrolledContainerRef,
  currentSortKey,
  expandedRow,
  relatedAppointments = {},
  isExpandable = false,
  loadingRelated = false,
  localStorageKey,
  getRowClassName,
}) => {
  const { tableRef, theadRef } = useStickyHeader({
    enabled: stickyHeader,
    appHeaderHeight,
    scrolledContainerRef,
  });

  const [selectedRow, setSelectedRow] = useState(null);

  // Load from localStorage on mount
  useEffect(() => {
    if (!localStorageKey || !data.length) return;

    const storedId = localStorage.getItem(localStorageKey);
    if (storedId) {
      const found = data.find((row) => String(row.id) === storedId);
      if (found) setSelectedRow(found);
    }
  }, [localStorageKey, data]);

  const handleSelectRow = (row, noResetBgColor) => {
    if (!localStorageKey) return;

    if (selectedRow === row && !noResetBgColor) {
      setSelectedRow(null);
      localStorage.removeItem(localStorageKey);
    } else {
      setSelectedRow(row);
      localStorage.setItem(localStorageKey, row.id);
    }
  };

  const [renderKey, setRenderKey] = useState(0);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null, // null = no sort, 'asc' = ascending, 'desc' = descending
  });
  const scrollContainerRef = useRef(null);

  useStickyScrollbar({
    enabled: stickyScrollbar,
    scrollContainerRef,
    dependencies: [data, headers],
  });

  useEffect(() => {
    if (currentSortKey) {
      setSortConfig({
        key: currentSortKey,
        direction: 'asc',
      });
    } else {
      setSortConfig({
        key: null,
        direction: null,
      });
    }
  }, [currentSortKey]);

  // Handle column sorting with custom behavior when onSort exists
  const handleSort = (headerKey, headerSort) => {
    if (!headerSort) return;

    const isCurrentColumn = sortConfig.key === headerKey;
    let newDirection;

    if (!isCurrentColumn) {
      newDirection = 'asc';
    } else {
      // When onSort exists, use two-state behavior: asc → null (skip desc)

      if (onSort) {
        switch (sortConfig.direction) {
          case null:
            newDirection = 'asc';
            break;
          case 'asc':
            newDirection = null;
            break;
          default:
            newDirection = 'asc';
        }
      } else {
        // Default behavior: no sort → asc → desc → no sort
        switch (sortConfig.direction) {
          case null:
            newDirection = 'asc';
            break;
          case 'asc':
            newDirection = 'desc';
            break;
          case 'desc':
            newDirection = null;
            break;
          default:
            newDirection = 'asc';
        }
      }
    }

    const newSortConfig = {
      key: newDirection ? headerKey : null,
      direction: newDirection,
    };

    // Call external sort handler if provided
    if (onSort) {
      onSort(newSortConfig.direction !== null ? newSortConfig.key : undefined);
    }

    setSortConfig(newSortConfig);
    setRenderKey((prev) => prev + 1);
  };

  const getSortValue = (item, column) => {
    if (column.sortValue && typeof column.sortValue === 'function') {
      return column.sortValue(item);
    }
    return column.field ? item[column.field] : item;
  };

  const isNumeric = (value) => {
    if (value == null || value === '') return false;
    return !isNaN(parseFloat(value)) && isFinite(value);
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction || !!onSort) {
      return data;
    }

    const column = headers.find((h) => (h.field || h) === sortConfig.key);

    if (!column) return data;

    const isAscending = sortConfig.direction === 'asc';

    return [...data].sort((a, b) => {
      let aValue = getSortValue(a, column) ?? '';
      let bValue = getSortValue(b, column) ?? '';

      const aIsNumeric = isNumeric(aValue);
      const bIsNumeric = isNumeric(bValue);

      if (aIsNumeric && bIsNumeric) {
        const numA = parseFloat(aValue);
        const numB = parseFloat(bValue);
        return isAscending ? numA - numB : numB - numA;
      }

      const strA = String(aValue).toLowerCase();
      const strB = String(bValue).toLowerCase();

      if (strA < strB) return isAscending ? -1 : 1;
      if (strA > strB) return isAscending ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig, headers, onSort]);

  const getSortIcon = (headerKey, headerSort) => {
    if (!headerSort) return null;

    // When onSort is provided, use currentSortKey for visual state
    const activeKey = onSort ? currentSortKey : sortConfig.key;
    const activeDirection = onSort ? 'asc' : sortConfig.direction; // Assume asc for external sort

    if (activeKey === headerKey) {
      switch (activeDirection) {
        case 'asc':
          return Icons.sortedNewUp;
        case 'desc':
          return Icons.sortedNewDown;
        case null:
        default:
          return Icons.sortNew;
      }
    }

    return Icons.sortNew;
  };

  if ((!data || data.length === 0) && emptyMessage !== '') {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          .hide-scrollbar::-webkit-scrollbar {
            display: none; /* Chrome, Safari, Opera */
          }

          @keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-out forwards;
}

.animate-slideDown {
  animation: slideDown 0.4s ease-out forwards;
}

.animate-slideDown-delay-1 {
  animation: slideDown 0.4s ease-out 0.05s forwards;
}

.animate-slideDown-delay-2 {
  animation: slideDown 0.4s ease-out 0.1s forwards;
}

        `}
      </style>
      <div className="table-container" ref={tableRef}>
        <div
          className="overflow-x-auto  hide-scrollbar"
          ref={scrollContainerRef}
          style={{
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE/Edge
          }}>
          <table
            key={renderKey}
            className="w-full border-separate pb-2"
            style={{
              minWidth: minWidth || '800px',
              borderSpacing: 0,
              isolation: 'isolate',
            }}>
            <thead
              ref={theadRef}
              className={`shadow-table-header bg-white relative z-99`}>
              <tr className="relative">
                {headers.map((header, index) => {
                  const headerKey = header.field || header || index;
                  const headerLabel = header.label || header;
                  const headerSort = header.sort || false;
                  const isSortable = headerSort === true;
                  const isLastColumn = index === headers.length - 1;
                  const isFirstColumn = index === 0;

                  return (
                    <th
                      key={headerKey}
                      className={`z-99 bg-white border border-solid border-border-primary px-2 text-center font-bold text-xs text-gray-600-light relative h-10 ${
                        isSortable
                          ? 'relative cursor-pointer group hover:bg-gray-50'
                          : ''
                      }`}
                      style={{
                        borderTopLeftRadius: isFirstColumn ? '4px' : '0',
                        borderTopRightRadius: isLastColumn ? '4px' : '0',
                        borderLeftWidth: isFirstColumn ? '1px' : '0',
                        borderRightWidth: isLastColumn ? '1px' : '0',
                        borderBottomWidth: '1px',
                      }}
                      onClick={() =>
                        isSortable && handleSort(headerKey, isSortable)
                      }>
                      <div
                        className={` flex w-fit m-auto items-center ${
                          header.textAlign ? header.textAlign : 'justify-center'
                        } ${
                          header.small ? 'max-w-min' : 'whitespace-nowrap '
                        } `}
                        style={{
                          minWidth:
                            headerLabel.length >= 14
                              ? '136px'
                              : headerLabel.length >= 8
                                ? '99px'
                                : headerLabel.length === 1
                                  ? 12
                                  : '54px',
                        }}>
                        {headerLabel}
                        {isSortable && (
                          <div
                            className={`pointer-events-none w-5 h-5 flex items-center justify-center transition-all duration-200 ${
                              (
                                onSort
                                  ? currentSortKey === headerKey
                                  : sortConfig.key === headerKey
                              )
                                ? ''
                                : 'hidden group-hover:block'
                            }`}>
                            <Icon
                              src={getSortIcon(headerKey, headerSort)}
                              width="18px"
                              height="18px"
                            />
                          </div>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sortedData.map((item, index) => {
                const cellData = headers.map((col) =>
                  col.component
                    ? col.component(item, index)
                    : item[col.field] || '-'
                );

                const uniqueKey = item.id
                  ? `${item.id}-${index}`
                  : `item-${index}`;
                const rowKey = `${item.container}-${index}`;
                const isExpanded =
                  isExpandable && expandedRow.rowKey === rowKey;
                const relatedData = isExpandable
                  ? relatedAppointments[rowKey] || []
                  : [];

                const rowZIndexClass = getRowClassName
                  ? getRowClassName(item, index)
                  : 'z-9';

                return isExpandable ? (
                  <React.Fragment key={uniqueKey}>
                    <tr>
                      <td colSpan={headers.length} className="h-2 p-0"></td>
                    </tr>
                    <tr
                      className={`${rowZIndexClass} ${
                        isExpanded
                          ? 'bg-border-light-secondary'
                          : selectedRow === item
                          ? 'bg-border-light-secondary'
                          : 'bg-white'
                      }  `}>
                      {cellData.map((cellContent, cellIndex) => {
                        const isLastCell = cellIndex === headers.length - 1;
                        const isFirstCell = cellIndex === 0;

                        return (
                          <td
                            onClick={() =>
                              handleSelectRow(
                                item,
                                headers[cellIndex].noResetBgColor
                              )
                            }
                            key={cellIndex}
                            className={`${
                              isExpanded
                                ? ' border-border-secondary'
                                : 'border-t border-border-tertiary'
                            } ${headers[cellIndex].noMaxWidth ? '' : 'max-w-128'}  whitespace-nowrap text-center px-2 h-12 align-middle text-xs text-gray-600-light font-light border border-solid `}
                            style={{
                              borderTopLeftRadius: isFirstCell ? '4px' : '0',
                              borderBottomLeftRadius:
                                isFirstCell && !isExpanded ? '4px' : '0',
                              borderTopRightRadius: isLastCell ? '4px' : '0',
                              borderBottomRightRadius:
                                isLastCell && !isExpanded ? '4px' : '0',
                              borderLeftWidth: isFirstCell ? '1px' : '0',
                              borderRightWidth: isLastCell ? '1px' : '0',
                              borderBottomColor: isExpanded ? '#E1E4EA' : '',
                              transition: ` border 300ms ease`,
                              ...(headers[cellIndex].maxCellWidth
                                ? { maxWidth: headers[cellIndex].maxCellWidth }
                                : {}),
                              boxShadow:
                                isExpanded &&
                                (isFirstCell
                                  ? '-7px 0 10px -7px rgba(0, 0, 0, 0.20)'
                                  : isLastCell
                                  ? '7px 0 10px -7px rgba(0, 0, 0, 0.20)'
                                  : ''),
                            }}>
                            {cellContent}
                          </td>
                        );
                      })}
                    </tr>
                    {isExpanded && (
                      <>
                        <tr className={`z-9 bg-white   `}>
                          <td
                            // style={{
                            //   boxShadow: '-7px 0 7px -7px rgba(0, 0, 0, 0.20)',
                            // }}
                            colSpan={headers.length}
                            className="p-0 opacity-0 animate-slideDown">
                            <div className="flex items-center gap-2 px-2 py-2 border border-solid border-border-secondary border-t-0 border-b-0 rounded-b-4">
                              <Icon
                                src={Icons.dangerInfo}
                                width="16px"
                                height="16px"
                              />
                              <p className="text-xs text-gray-600 m-0">
                                Other appointments for{' '}
                                <span className="font-bold text-gray-1000">
                                  {item.container}
                                </span>
                              </p>
                            </div>
                          </td>
                        </tr>
                        {/* Related rows */}
                        {!loadingRelated
                          ? relatedData.map((relatedItem, relatedIndex) => {
                              const relatedCellData = headers.map((col) =>
                                col.component
                                  ? col.component(
                                      relatedItem,
                                      relatedIndex,
                                      true
                                    )
                                  : relatedItem[col.field] || '-'
                              );

                              const getAnimationClass = () => {
                                if (relatedIndex % 3 === 0)
                                  return 'animate-slideDown';
                                if (relatedIndex % 3 === 1)
                                  return 'animate-slideDown-delay-1';
                                return 'animate-slideDown-delay-2';
                              };

                              const relatedRowZIndexClass = getRowClassName
                                ? getRowClassName(relatedItem, relatedIndex)
                                : 'z-9';

                              return (
                                <React.Fragment key={`related-${relatedIndex}`}>
                                  {relatedIndex !== 0 && (
                                    <tr className="border-b border-border-secondary border-solid">
                                      <td
                                        colSpan={headers.length}
                                        className="p-0"></td>
                                    </tr>
                                  )}
                                  <tr
                                    className={`${relatedRowZIndexClass} bg-white  opacity-0 ${getAnimationClass()}`}>
                                    {relatedCellData.map(
                                      (cellContent, cellIndex) => {
                                        const isLastCell =
                                          cellIndex === headers.length - 1;
                                        const isFirstCell = cellIndex === 0;
                                        const isLastRelatedRow =
                                          relatedIndex ===
                                          relatedData.length - 1;

                                        return (
                                          <td
                                            key={cellIndex}
                                            className={`${
                                              isExpanded
                                                ? 'border-border-secondary'
                                                : 'border-border-tertiary'
                                            } ${headers[cellIndex].noMaxWidth ? '' : 'max-w-128'}  whitespace-nowrap text-center px-2 h-12 align-middle text-xs text-gray-600-light font-light border border-solid `}
                                            style={{
                                              borderLeftWidth: isFirstCell
                                                ? '1px'
                                                : '0',
                                              borderRightWidth: isLastCell
                                                ? '1px'
                                                : '0',
                                              borderTopWidth: '1px',
                                              borderBottomWidth:
                                                !isLastRelatedRow
                                                  ? '0px'
                                                  : '1px',
                                              borderTopColor: isExpanded
                                                ? '#E1E4EA'
                                                : '',
                                              borderBottomColor:
                                                !isLastRelatedRow
                                                  ? '#E1E4EA'
                                                  : '',
                                              borderBottomLeftRadius:
                                                isFirstCell && isLastRelatedRow
                                                  ? '4px'
                                                  : '0',
                                              borderBottomRightRadius:
                                                isLastCell && isLastRelatedRow
                                                  ? '4px'
                                                  : '0',
                                              boxShadow:
                                                isLastRelatedRow &&
                                                (isFirstCell
                                                  ? '-7px 0 10px -7px rgba(0, 0, 0, 0.20)'
                                                  : isLastCell
                                                  ? '7px 0 10px -7px rgba(0, 0, 0, 0.20)'
                                                  : ''),
                                              ...(headers[cellIndex].maxCellWidth
                                                ? {
                                                    maxWidth:
                                                      headers[cellIndex]
                                                        .maxCellWidth,
                                                  }
                                                : {}),
                                            }}>
                                            {cellContent}
                                          </td>
                                        );
                                      }
                                    )}
                                  </tr>
                                </React.Fragment>
                              );
                            })
                          : Array.from({
                              length: expandedRow.count,
                            }).map((relatedItem, relatedIndex) => {
                              const relatedCellData = headers.map((col) => '-');

                              const getAnimationClass = () => {
                                if (relatedIndex % 3 === 0)
                                  return 'animate-slideDown';
                                if (relatedIndex % 3 === 1)
                                  return 'animate-slideDown-delay-1';
                                return 'animate-slideDown-delay-2';
                              };

                              return (
                                <React.Fragment key={`related-${relatedIndex}`}>
                                  {relatedIndex !== 0 && (
                                    <tr className="border-b border-border-secondary border-solid">
                                      <td
                                        colSpan={headers.length}
                                        className="p-0"></td>
                                    </tr>
                                  )}
                                  <tr
                                    className={`z-9 bg-white  opacity-0 ${getAnimationClass()}`}>
                                    {relatedCellData.map(
                                      (cellContent, cellIndex) => {
                                        const isLastCell =
                                          cellIndex === headers.length - 1;
                                        const isFirstCell = cellIndex === 0;
                                        const isLastRelatedRow =
                                          relatedIndex ===
                                          expandedRow.count - 1;

                                        return (
                                          <td
                                            key={cellIndex}
                                            className={`${
                                              isExpanded
                                                ? 'border-border-secondary'
                                                : 'border-border-tertiary'
                                            } ${headers[cellIndex].noMaxWidth ? '' : 'max-w-128'}  whitespace-nowrap text-center px-2 h-12 align-middle text-xs text-gray-600-light font-light border border-solid `}
                                            style={{
                                              borderLeftWidth: isFirstCell
                                                ? '1px'
                                                : '0',
                                              borderRightWidth: isLastCell
                                                ? '1px'
                                                : '0',
                                              borderTopWidth: '1px',
                                              borderBottomWidth:
                                                !isLastRelatedRow
                                                  ? '0px'
                                                  : '1px',
                                              borderTopColor: isExpanded
                                                ? '#E1E4EA'
                                                : '',
                                              borderBottomColor:
                                                !isLastRelatedRow
                                                  ? '#E1E4EA'
                                                  : '',
                                              borderBottomLeftRadius:
                                                isFirstCell && isLastRelatedRow
                                                  ? '4px'
                                                  : '0',
                                              borderBottomRightRadius:
                                                isLastCell && isLastRelatedRow
                                                  ? '4px'
                                                  : '0',
                                              boxShadow:
                                                isLastRelatedRow &&
                                                (isFirstCell
                                                  ? '-7px 0 10px -7px rgba(0, 0, 0, 0.20)'
                                                  : isLastCell
                                                  ? '7px 0 10px -7px rgba(0, 0, 0, 0.20)'
                                                  : ''),
                                              ...(headers[cellIndex].maxCellWidth
                                                ? {
                                                    maxWidth:
                                                      headers[cellIndex]
                                                        .maxCellWidth,
                                                  }
                                                : {}),
                                            }}>
                                            {cellIndex !== 0 && (
                                              <div
                                                style={{
                                                  background:
                                                    'linear-gradient(90deg, rgba(202, 207, 216, 0.20) 33.05%, rgba(225, 228, 234, 0.00) 110.96%)',
                                                }}
                                                className="h-5 w-full  m-auto animate-pulse"
                                              />
                                            )}
                                          </td>
                                        );
                                      }
                                    )}
                                  </tr>
                                </React.Fragment>
                              );
                            })}
                      </>
                    )}
                  </React.Fragment>
                ) : (
                  <React.Fragment key={uniqueKey}>
                    <tr>
                      <td colSpan={headers.length} className="h-2 p-0"></td>
                    </tr>

                    <tr
                      className={`${rowZIndexClass} bg-white  shadow-table-data`}>
                      {cellData.map((cellContent, cellIndex) => {
                        const isLastCell = cellIndex === headers.length - 1;
                        const isFirstCell = cellIndex === 0;

                        return (
                          <td
                            key={cellIndex}
                            className={`${
                              headers[cellIndex]?.noMaxWidth ? '' : 'max-w-128'
                            }   whitespace-nowrap text-center px-2 h-12 align-middle text-xs text-gray-600-light font-light border border-solid border-border-tertiary`}
                            style={{
                              borderTopLeftRadius: isFirstCell ? '4px' : '0',
                              borderBottomLeftRadius: isFirstCell ? '4px' : '0',
                              borderTopRightRadius: isLastCell ? '4px' : '0',
                              borderBottomRightRadius: isLastCell ? '4px' : '0',
                              borderLeftWidth: isFirstCell ? '1px' : '0',
                              borderRightWidth: isLastCell ? '1px' : '0',
                              ...(headers[cellIndex]?.maxCellWidth
                                ? { maxWidth: headers[cellIndex].maxCellWidth }
                                : {}),
                            }}>
                            {cellContent}
                          </td>
                        );
                      })}
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {stickyScrollbar && (
          <div
            id="sticky-native-scrollbar"
            className="bg-transparent fixed bottom-0 h-4 z-99  overflow-x-auto overflow-y-hidden hidden">
            <div
              style={{
                height: '1px',
              }}
              className="bg-transparent w-full"
            />
          </div>
        )}
      </div>
    </>
  );
};
